import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getLeoTimestamp,
  LeoTimestamp,
  getString,
  getOptionalString,
  getList,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SubmittedResponse, SubmittedResponseDTO, SubmittedResponseEnums } from "./submittedResponse";
import { Respondent, RespondentDTO } from "./respondent";
import { RespondentColumnDetails, RespondentColumnDetailsDTO } from "../types/respondentColumnDetails";
/* eslint-disable import/export */
export abstract class GetIndividualSubmittedResponsesRPC implements
  LeoRPC<GetIndividualSubmittedResponsesRPC.Request, GetIndividualSubmittedResponsesRPC.Response, GetIndividualSubmittedResponsesRPC.Errors.Errors>
{
  abstract execute(
    request: GetIndividualSubmittedResponsesRPC.Request
  ): Promise<LeoRPCResult<GetIndividualSubmittedResponsesRPC.Response, GetIndividualSubmittedResponsesRPC.Errors.Errors>>;
}

export namespace GetIndividualSubmittedResponsesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondentId: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetIndividualSubmittedResponsesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondentId: number;
  
  
    constructor(
    projectId: LeoUUID, 
    respondentId: number
    ) {
      this.projectId = projectId;
      this.respondentId = respondentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondentId = getInt32(dto, "respondentId", InvalidRequestRespondentIdError);
      return new Request(
        projectId,
        respondentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondentId: this.respondentId
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetIndividualSubmittedResponsesRPC(projectId='${this.projectId}',respondentId='${this.respondentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyEndTime: string;
    browser?: string | null;
    deviceType?: string | null;
    submittedResponses: SubmittedResponseDTO[];
    respondentDetails: RespondentDTO;
    respondentColumnDetails: RespondentColumnDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetIndividualSubmittedResponsesResponse: boolean = true;
  
    readonly surveyEndTime: LeoTimestamp;
    readonly browser: string | null;
    readonly deviceType: string | null;
    readonly submittedResponses: SubmittedResponse[];
    readonly respondentDetails: Respondent;
    readonly respondentColumnDetails: RespondentColumnDetails[];
  
  
    constructor(
    surveyEndTime: LeoTimestamp, 
    browser: string | null = null, 
    deviceType: string | null = null, 
    submittedResponses: SubmittedResponse[], 
    respondentDetails: Respondent, 
    respondentColumnDetails: RespondentColumnDetails[]
    ) {
      this.surveyEndTime = surveyEndTime;
      if ( browser !== undefined && browser !== null) {
        Response.validateBrowser(browser);
      }
      this.browser = browser;
      if ( deviceType !== undefined && deviceType !== null) {
        Response.validateDeviceType(deviceType);
      }
      this.deviceType = deviceType;
      this.submittedResponses = submittedResponses;
      this.respondentDetails = respondentDetails;
      this.respondentColumnDetails = respondentColumnDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyEndTime = getLeoTimestamp(dto, "surveyEndTime", InvalidResponseSurveyEndTimeError);
      const browser = getOptionalString(dto, "browser", InvalidResponseBrowserError);
      const deviceType = getOptionalString(dto, "deviceType", InvalidResponseDeviceTypeError);
      const submittedResponses = getList<SubmittedResponseDTO, SubmittedResponse>(
        dto,
        "submittedResponses",
        SubmittedResponse.fromDTO,
        InvalidResponseSubmittedResponsesError
      );
      const respondentDetails = Respondent.fromDTO(getObject(dto, "respondentDetails", InvalidResponseRespondentDetailsError));
      const respondentColumnDetails = getList<RespondentColumnDetailsDTO, RespondentColumnDetails>(
        dto,
        "respondentColumnDetails",
        RespondentColumnDetails.fromDTO,
        InvalidResponseRespondentColumnDetailsError
      );
      return new Response(
        surveyEndTime,
        browser,
        deviceType,
        submittedResponses,
        respondentDetails,
        respondentColumnDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyEndTime: this.surveyEndTime.toDTO(),
          submittedResponses: this.submittedResponses.map((e: SubmittedResponse) => { return e.toDTO() }),
          respondentDetails: this.respondentDetails.toDTO(),
          respondentColumnDetails: this.respondentColumnDetails.map((e: RespondentColumnDetails) => { return e.toDTO() })
      };
      if (this.browser !== undefined && this.browser !== null){
        dto.browser = this.browser;
      }
      if (this.deviceType !== undefined && this.deviceType !== null){
        dto.deviceType = this.deviceType;
      }
      return dto;
    }
    private static validateBrowser = (browser: string): void => {
      if(browser.trim().length === 0) {
        throw new InvalidResponseBrowserNonEmptyError(
          `Attribute browser cannot be an empty string.`
        );
      }
    }
    private static validateDeviceType = (deviceType: string): void => {
      if(deviceType.trim().length === 0) {
        throw new InvalidResponseDeviceTypeNonEmptyError(
          `Attribute deviceType cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetIndividualSubmittedResponses(surveyEndTime='${this.surveyEndTime}',browser='${this.browser}',deviceType='${this.deviceType}',submittedResponses='${this.submittedResponses}',respondentDetails='${this.respondentDetails}',respondentColumnDetails='${this.respondentColumnDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidRespondentId = "INVALID_RESPONDENT_ID",
    SurveyNotSubmitted = "SURVEY_NOT_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetIndividualSubmittedResponsesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidRespondentIdDTO {
      code: string;
    }
    
    export class InvalidRespondentId extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentId;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdDTO {
        const dto: InvalidRespondentIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentId.prototype.toString = function toString(): string {
      return `GetIndividualSubmittedResponsesRPC.INVALID_RESPONDENT_ID()`;
    };
    
    
    export interface SurveyNotSubmittedDTO {
      code: string;
    }
    
    export class SurveyNotSubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyNotSubmitted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNotSubmitted;
        super(code);
      }
    
    
      toDTO(): SurveyNotSubmittedDTO {
        const dto: SurveyNotSubmittedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNotSubmitted.prototype.toString = function toString(): string {
      return `GetIndividualSubmittedResponsesRPC.SURVEY_NOT_SUBMITTED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidRespondentId | SurveyNotSubmitted;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidRespondentIdDTO | SurveyNotSubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidRespondentId:
          return new InvalidRespondentId();
        case RPCError.SurveyNotSubmitted:
          return new SurveyNotSubmitted();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetIndividualSubmittedResponsesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentIdError extends GetIndividualSubmittedResponsesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentIdError";
  }
}

export class InvalidResponseSurveyEndTimeError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyEndTimeError";
  }
}

export class InvalidResponseBrowserError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBrowserError";
  }
}

export class InvalidResponseBrowserNonEmptyError extends InvalidResponseBrowserError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBrowserNonEmptyError";
  }
}

export class InvalidResponseDeviceTypeError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeviceTypeError";
  }
}

export class InvalidResponseDeviceTypeNonEmptyError extends InvalidResponseDeviceTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeviceTypeNonEmptyError";
  }
}

export class InvalidResponseSubmittedResponsesError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSubmittedResponsesError";
  }
}

export class InvalidResponseRespondentDetailsError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentDetailsError";
  }
}

export class InvalidResponseRespondentColumnDetailsError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentColumnDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidRespondentIdError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdError";
  }
}

export class InvalidResponseSurveyNotSubmittedError extends GetIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNotSubmittedError";
  }
}

